import { useEffect, useMemo, useRef, useState } from "react"
import toast from "react-hot-toast"
import { AlertCircle, ArrowLeft, CheckCircle, Check, ChevronDown, Download, Hourglass, Pencil, RefreshCw, Sparkles, Trash2 } from "lucide-react"
import { collection, deleteDoc, doc, onSnapshot, orderBy, query, setDoc } from "firebase/firestore"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/Page/AnnotationLayer.css"
import "react-pdf/dist/Page/TextLayer.css"
import { db } from "../../firebaseConfig"
import { initials } from "../JobCard"

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs"

export function ResumeCanvas({ url, loading }) {
  const containerRef = useRef(null)
  const [width, setWidth] = useState(0)
  const [numPages, setNumPages] = useState(0)
  const [err, setErr] = useState(null)
  useEffect(() => {
    if (!containerRef.current) return
    const el = containerRef.current
    const update = () => setWidth(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const fileProp = useMemo(() => ({ url: url + (url.includes("?") ? "&" : "?") + "v=1" }), [url])
  return (
    <div ref={containerRef} style={{ width: "min(8.5in, 100%)", margin: "0 auto" }}>
      <Document
        file={fileProp}
        onLoadSuccess={({ numPages: n }) => { setErr(null); setNumPages(n) }}
        onLoadError={e => { console.error("[ResumeCanvas] load error", e); setErr(e) }}
        loading={loading ?? <div style={{ padding: 24, textAlign: "center", color: "#6B7280", fontSize: 13 }}>Loading resume…</div>}
        error={<div style={{ padding: 24, textAlign: "center", color: "#B91C1C", fontSize: 13 }}>Could not load resume{err?.message ? `: ${err.message}` : ""}</div>}
        noData=""
      >
        {width > 0 && Array.from({ length: numPages }, (_, i) => (
          <div key={i} style={{ marginBottom: i < numPages - 1 ? 16 : 0, borderRadius: 12, boxShadow: "0 4px 28px rgba(18,18,18,0.08)", overflow: "hidden", background: "#FFFFFF" }}>
            <Page pageNumber={i + 1} width={width} renderAnnotationLayer={false} renderTextLayer={false} />
          </div>
        ))}
      </Document>
    </div>
  )
}

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"
const APPLICATIONS_API = "https://jobscanner-e1dd4.ue.r.appspot.com/applications"
export const APPLY_AGENT_API = "https://resolut-apply-agent-production.up.railway.app"
export const DEFAULT_WORKDAY_PASSWORD = "qweDSA@998rxke"
const PROGRESS_STAGES = ["Optimise", "Resume", "Filling", "Submitting", "Completed"]

function parseDate(raw) {
  if (!raw) return new Date()
  if (typeof raw?.toDate === "function") return raw.toDate()
  const date = new Date(raw)
  return Number.isNaN(date.getTime()) ? new Date() : date
}

function ago(date) {
  const diff = Date.now() - date.getTime()
  const mins = Math.max(0, Math.floor(diff / 60000))
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return days === 1 ? "yesterday" : `${days} days ago`
  const months = Math.floor(days / 30)
  return months === 1 ? "a month ago" : `${months} months ago`
}

function parseStatus(status = "") {
  return [
    "pending_optimization",
    "pending_resume_review",
    "processing",
    "completed",
    "failed",
    "job_expired",
    "pending_verification",
    "credentials_error",
    "additional_information_required",
    "skipped",
  ].includes(status) ? status : "queued"
}

function normalizeApplicationDoc(docSnap) {
  const data = docSnap.data() || {}
  const pendingQuestion = data.pendingQuestion || {}
  return {
    ...data,
    id: docSnap.id,
    jobTitle: data.jobTitle || "",
    company: data.company || "",
    logoUrl: data.logoUrl || "",
    jobUrl: data.jobUrl || "",
    description: data.description || "",
    reviewSections: Array.isArray(data.reviewSections) ? data.reviewSections : [],
    pendingQuestionLabel: pendingQuestion.label || "",
    pendingQuestionKey: pendingQuestion.key || "",
    appliedAt: parseDate(data.appliedAt),
    status: parseStatus(data.status),
  }
}

function buckets(applications) {
  return {
    review: applications.filter(app => [
      "pending_optimization",
      "pending_resume_review",
      "additional_information_required",
      "pending_verification",
      "credentials_error",
    ].includes(app.status)),
    inProgress: applications.filter(app => ["queued", "processing"].includes(app.status)),
    submitted: applications.filter(app => ["completed", "job_expired"].includes(app.status)),
    failed: applications.filter(app => app.status === "failed"),
  }
}

const FILTERS = [
  { id: "review", label: "Review" },
  { id: "inProgress", label: "In Progress" },
  { id: "submitted", label: "Submitted" },
  { id: "failed", label: "Failed" },
]

function resumeMeta(status) {
  if (status === "pending_optimization") return { label: "Optimising", tone: "gold" }
  if (["pending_resume_review", "additional_information_required", "pending_verification", "credentials_error"].includes(status)) {
    return { label: "Review", tone: "orange" }
  }
  if (["job_expired", "skipped", "failed"].includes(status)) return { label: "Skipped", tone: "muted" }
  if (["queued", "processing"].includes(status)) return { label: "Ready", tone: "green" }
  return { label: "Default", tone: "muted" }
}

function statusMeta(status) {
  switch (status) {
    case "pending_optimization": return { label: "Optimising", tone: "gold" }
    case "pending_resume_review": return { label: "Needs Review", tone: "orange" }
    case "additional_information_required": return { label: "Screening Questions Required", tone: "gold" }
    case "pending_verification": return { label: "Verify Email", tone: "gold" }
    case "credentials_error": return { label: "Reset Password", tone: "gold" }
    case "processing": return { label: "Applying", tone: "orange" }
    case "completed": return { label: "Submitted", tone: "green" }
    case "failed": return { label: "Failed", tone: "red" }
    case "job_expired": return { label: "Skipped", tone: "muted" }
    case "skipped": return { label: "Skipped", tone: "muted" }
    default: return { label: "In flight", tone: "muted" }
  }
}

function badgeStyle(tone) {
  if (tone === "green") return { background: "#F5F0E6", color: "#121212", dot: "#121212" }
  if (tone === "orange") return { background: "#FFF7ED", color: "#D95F19", dot: "#D95F19" }
  if (tone === "gold") return { background: "#FEF3E6", color: "#D95F19", dot: "#D95F19" }
  if (tone === "red") return { background: "#FEF2F2", color: "#B91C1C", dot: "#EF4444" }
  return { background: "#F4F5F7", color: "#7B8494", dot: "#A6B0BF" }
}

function Pill({ meta }) {
  const s = badgeStyle(meta.tone)
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 24, padding: "0 10px", borderRadius: 999, background: s.background, color: s.color, fontSize: 11.5, fontWeight: 700, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot }} />
      {meta.label}
    </span>
  )
}

function CompanyLogo({ app }) {
  const [err, setErr] = useState(false)
  if (app.logoUrl && !err) {
    return (
      <span style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid #f3f4f6", background: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
        <img src={app.logoUrl} alt="" onError={() => setErr(true)} style={{ width: 32, height: 32, objectFit: "contain" }} />
      </span>
    )
  }
  return (
    <span style={{ width: 32, height: 32, borderRadius: 8, border: "1px solid #f3f4f6", background: "#F8FAFC", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <span style={{ fontSize: 10.5, fontWeight: 800, color: "#172033" }}>{initials(app.company)}</span>
    </span>
  )
}

const EMPTY_COPY = {
  review: {
    tag: "Review",
    title: "Nothing waiting on you",
    body: "When the agent finishes optimising a resume, it'll land here first for your approval.",
    icon: Sparkles,
    hint: "Tailored resumes appear within a minute of starting a role.",
  },
  inProgress: {
    tag: "In Progress",
    title: "No applications in flight",
    body: "Approve a tailored resume and the agent will start filling the application. You'll see each step show up here live.",
    icon: Hourglass,
    hint: "Agent fills forms autonomously — no need to babysit.",
  },
  submitted: {
    tag: "Submitted",
    title: "Nothing submitted yet",
    body: "Completed applications move here. You'll see the company, role, and when the submission went through.",
    icon: CheckCircle,
    hint: "Keep this tab open while the agent is applying — results stream in.",
  },
  failed: {
    tag: "Failed",
    title: "Nothing failed",
    body: "If an application gets stuck or a role expires, you'll find it here with a one-click retry.",
    icon: AlertCircle,
    hint: "You can always restart a failed run with the same tailored resume.",
  },
}

function EmptyState({ filter }) {
  const copy = EMPTY_COPY[filter] || { tag: "", title: "No applications", body: "This list is empty.", icon: Sparkles }
  const Icon = copy.icon
  return (
    <div style={{ padding: "72px 24px 96px", textAlign: "center", maxWidth: 460, margin: "0 auto" }}>
      <div style={{ margin: "0 auto 20px", color: "#121212", display: "inline-flex" }}>
        <Icon size={32} strokeWidth={1.5} />
      </div>
      <p style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#18181b", letterSpacing: "-0.02em", lineHeight: 1.2 }}>{copy.title}</p>
      <p style={{ margin: "10px auto 0", fontSize: 14, lineHeight: 1.6, color: "#71717a" }}>{copy.body}</p>
      {copy.hint && (
        <div style={{ marginTop: 28, display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px", borderRadius: 10, border: "1px dashed #e4e4e7", background: "#FFFFFF", fontSize: 12, color: "#52525b", fontWeight: 500 }}>
          <Sparkles size={13} strokeWidth={1.8} style={{ color: "#D95F19" }} />
          {copy.hint}
        </div>
      )}
    </div>
  )
}

function ProgressRail({ stage, color = "#D95F19" }) {
  const current = Math.max(1, Math.min(5, stage))
  return (
    <div>
      <div style={{ display: "flex", gap: 5, marginTop: 16 }}>
        {PROGRESS_STAGES.map((_, i) => (
          <div key={i} style={{ flex: 1, height: 4, borderRadius: 4, background: i === current - 1 ? color : i < current - 1 ? "#0F172A" : "#E9EAED" }} />
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 11, color: "#94A3B8", fontWeight: 600 }}>
        <span>{PROGRESS_STAGES[current - 1]}</span>
        <span>{current} of 5</span>
      </div>
    </div>
  )
}

function PulsingDot({ color = "#D95F19" }) {
  return (
    <span style={{ position: "relative", width: 8, height: 8, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{ position: "absolute", width: 8, height: 8, borderRadius: "50%", background: color, animation: "pulseDot 1.5s infinite" }} />
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, zIndex: 1 }} />
    </span>
  )
}

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 260 }}>
      <div style={{ width: 24, height: 24, border: "2px solid #E5E7EB", borderTopColor: "#111827", borderRadius: "50%", animation: "spin 0.75s linear infinite" }} />
    </div>
  )
}

function Shimmer({ w = "100%", h = 12, r = 6, style }) {
  return (
    <span className="apps-shimmer" style={{ display: "inline-block", width: w, height: h, borderRadius: r, ...style }} />
  )
}

function TableShimmer({ rows = 8 }) {
  return (
    <table className="apps-table" style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontFamily: FF, fontSize: 13 }}>
      <colgroup>
        <col style={{ width: "42%" }} />
        <col style={{ width: "12%" }} />
        <col style={{ width: "18%" }} />
        <col style={{ width: "12%" }} />
        <col style={{ width: "16%" }} />
      </colgroup>
      <thead>
        <tr>
          {["Company", "Resume", "Status", "Applied", ""].map((heading, index) => (
            <th key={heading || "actions"} style={{ borderBottom: "1px solid #e5e7eb", background: "#f9fafb", padding: index === 4 ? "10px 8px" : "10px 16px", textAlign: "left", fontWeight: 500, fontSize: 10.5, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {heading}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }, (_, i) => (
          <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
            <td style={{ padding: "12px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Shimmer w={32} h={32} r={8} />
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
                  <Shimmer w="55%" h={11} />
                  <Shimmer w="78%" h={10} />
                </div>
              </div>
            </td>
            <td style={{ padding: "12px 16px" }}><Shimmer w={64} h={20} r={999} /></td>
            <td style={{ padding: "12px 16px" }}><Shimmer w={80} h={20} r={999} /></td>
            <td style={{ padding: "12px 16px" }}><Shimmer w={60} h={10} /></td>
            <td style={{ padding: "12px 8px" }}>
              <div style={{ display: "flex", justifyContent: "flex-end" }}><Shimmer w={72} h={24} r={6} /></div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function ReviewPaneShimmer() {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 16, background: "#FAFAFA", border: "1px solid #F3F4F6", marginBottom: 18 }}>
        <Shimmer w={56} h={56} r={999} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          <Shimmer w={92} h={14} r={999} />
          <Shimmer w="80%" h={11} />
        </div>
      </div>
      <Shimmer w={160} h={11} style={{ marginBottom: 12 }} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 22 }}>
        {[72, 90, 56, 110, 68, 84, 60, 96].map((w, i) => <Shimmer key={i} w={w} h={22} r={999} />)}
      </div>
      <Shimmer w={140} h={11} style={{ marginBottom: 12 }} />
      {[0, 1].map(i => (
        <div key={i} style={{ marginBottom: 10, border: "1px solid #F3F4F6", borderRadius: 12, padding: "12px 14px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Shimmer w="55%" h={13} />
            <Shimmer w="35%" h={11} />
          </div>
        </div>
      ))}
    </div>
  )
}

function SectionLabel({ children, count }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "22px 0 10px", fontSize: 11, color: "#9CA3AF", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>
      <span>{children}</span>
      {count != null && <span style={{ color: "#6B7280", letterSpacing: 0 }}>({count})</span>}
    </div>
  )
}

function KeywordChip({ label, matched, muted }) {
  const bg = matched ? "#F5F0E6" : muted ? "#F3F4F6" : "#FEF2F2"
  const fg = matched ? "#121212" : muted ? "#6B7280" : "#991B1B"
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 9px", borderRadius: 999, background: bg, color: fg, fontSize: 11.5, fontWeight: 650, lineHeight: 1.2 }}>
      {matched && <Check size={11} />} {label}
    </span>
  )
}

function MatchHero({ percentage, matched, total }) {
  const pct = Math.max(0, Math.min(100, percentage || 0))
  const tone = pct >= 70 ? { bg: "#F5F0E6", fg: "#121212", label: "Strong match" } : pct >= 40 ? { bg: "#FEF3E6", fg: "#D95F19", label: "Good match" } : { bg: "#FEF2F2", fg: "#991B1B", label: "Needs work" }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 16, background: "#FAFAFA", border: "1px solid #F3F4F6" }}>
      <div style={{ width: 56, height: 56, borderRadius: "50%", background: `conic-gradient(${tone.fg} ${pct * 3.6}deg, #E5E7EB 0)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: "#111827" }}>{pct}%</div>
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "inline-flex", alignItems: "center", height: 22, padding: "0 10px", borderRadius: 999, background: tone.bg, color: tone.fg, fontSize: 11.5, fontWeight: 750 }}>{tone.label}</div>
        <div style={{ marginTop: 6, fontSize: 13, color: "#4B5563", lineHeight: 1.4 }}><strong style={{ color: "#111827" }}>{matched}</strong> of <strong style={{ color: "#111827" }}>{total}</strong> keywords on your tailored resume</div>
      </div>
    </div>
  )
}

function Collapsible({ title, count, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div style={{ marginBottom: 8 }}>
      <button type="button" onClick={() => setOpen(v => !v)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "8px 2px", border: 0, background: "transparent", color: "#374151", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
        <span style={{ display: "inline-block", width: 10, color: "#9CA3AF", transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .15s" }}>▸</span>
        <span>{title}</span>
        {count != null && <span style={{ color: "#9CA3AF", fontWeight: 600 }}>({count})</span>}
      </button>
      {open && <div style={{ paddingBottom: 6 }}>{children}</div>}
    </div>
  )
}

const CHANGE_TYPE_LABEL = {
  rewrite: "Rewrite",
  insert:  "Added",
  tighten: "Trimmed",
  remove:  "Removed",
}
const CHANGE_TYPE_DOT = {
  rewrite: "#121212",
  insert:  "#D95F19",
  tighten: "#9CA3AF",
  remove:  "#B44747",
}

function ChangeEntry({ change, isLast }) {
  const [open, setOpen] = useState(false)
  const typeLabel = CHANGE_TYPE_LABEL[change?.type] || "Edit"
  const dotColor  = CHANGE_TYPE_DOT[change?.type] || "#121212"
  const before = (change?.before || "").trim()
  const after  = (change?.after  || "").trim()
  const showBefore = change?.type === "remove" || (before && change?.type !== "insert")
  const showAfter  = after && change?.type !== "remove"
  const hasDetail  = showBefore || showAfter
  const keywords   = Array.isArray(change?.keywords) ? change.keywords.filter(Boolean) : []
  const headline   = change?.reason || change?.location || "Edit"
  const subline    = change?.reason ? (change?.location || change?.section || "") : (change?.section || "")

  return (
    <div style={{ padding: "12px 0", borderBottom: isLast ? "0" : "1px solid #F3F4F6" }}>
      <button
        type="button"
        onClick={() => hasDetail && setOpen(v => !v)}
        style={{
          width: "100%", display: "flex", alignItems: "flex-start", gap: 10,
          background: "transparent", border: 0, padding: 0, textAlign: "left",
          cursor: hasDetail ? "pointer" : "default",
        }}
      >
        <span style={{
          flexShrink: 0, marginTop: 7,
          width: 6, height: 6, borderRadius: 999,
          background: dotColor,
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13, color: "#111827", fontWeight: 650,
            lineHeight: 1.5, letterSpacing: "-0.005em",
          }}>
            {headline}
          </div>
          <div style={{
            marginTop: 3, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
            fontSize: 11.5, color: "#9CA3AF",
          }}>
            {subline && <span>{subline}</span>}
            {subline && <span style={{ color: "#D1D5DB" }}>·</span>}
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              {typeLabel}
            </span>
            {keywords.map((kw, i) => (
              <span key={i} style={{
                padding: "1px 7px", borderRadius: 999,
                background: "#F5F0E6", color: "#121212",
                fontSize: 10.5, fontWeight: 650,
              }}>+ {kw}</span>
            ))}
          </div>
        </div>
        {hasDetail && (
          <ChevronDown
            size={14}
            style={{
              flexShrink: 0, marginTop: 4, color: "#9CA3AF",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform .15s",
            }}
          />
        )}
      </button>

      {open && hasDetail && (
        <div style={{
          marginTop: 10, marginLeft: 16,
          display: "flex", flexDirection: "column", gap: 6,
        }}>
          {showBefore && before && (
            <div style={{
              padding: "8px 12px", borderRadius: 10,
              background: "#FAFAFA",
              fontSize: 12.5, lineHeight: 1.55, color: "#6B7280",
              textDecoration: "line-through", textDecorationColor: "#D1D5DB",
              whiteSpace: "pre-wrap",
            }}>{before}</div>
          )}
          {showAfter && (
            <div style={{
              padding: "8px 12px", borderRadius: 10,
              background: "#F5F0E6",
              fontSize: 12.5, lineHeight: 1.55, color: "#121212",
              whiteSpace: "pre-wrap",
            }}>{after}</div>
          )}
        </div>
      )}
    </div>
  )
}

function OptimisingShimmer({ currentAction }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
        <PulsingDot />
        <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#D95F19" }}>Optimising your resume</div>
          <div style={{ fontSize: 12.5, color: "#6B7280", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {currentAction}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 16, background: "#FAFAFA", border: "1px solid #F3F4F6", marginBottom: 22 }}>
        <Shimmer w={56} h={56} r={999} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          <Shimmer w={96} h={14} r={999} />
          <Shimmer w="82%" h={11} />
        </div>
      </div>

      <Shimmer w={170} h={11} style={{ marginBottom: 10 }} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 24 }}>
        {[82, 100, 60, 118, 72, 92, 66, 104].map((w, i) => <Shimmer key={i} w={w} h={22} r={999} />)}
      </div>

      <Shimmer w={132} h={11} style={{ marginBottom: 14 }} />
      {[0, 1, 2, 3].map(i => (
        <div key={i} style={{ display: "flex", gap: 10, padding: "12px 0", borderBottom: i === 3 ? 0 : "1px solid #F3F4F6" }}>
          <Shimmer w={6} h={6} r={999} style={{ flexShrink: 0, marginTop: 7 }} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <Shimmer w="86%" h={11} />
            <Shimmer w="40%" h={9} />
          </div>
        </div>
      ))}
    </div>
  )
}

function highlightDescription(text, keywords) {
  if (!text) return null
  const items = Array.isArray(keywords) ? keywords.filter(k => k && k.keyword) : []
  if (items.length === 0) return <span>{text}</span>
  const escaped = items.map(k => k.keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
  const re = new RegExp(`(${escaped.join("|")})`, "gi")
  const map = new Map(items.map(k => [k.keyword.toLowerCase(), k.matched]))
  const parts = text.split(re)
  return parts.map((part, i) => {
    const matched = map.get(part.toLowerCase())
    if (matched === undefined) return <span key={i}>{part}</span>
    const bg = matched ? "#F5F0E6" : "#FEF2F2"
    const fg = matched ? "#121212" : "#991B1B"
    return <mark key={i} style={{ background: bg, color: fg, borderRadius: 3, padding: "0 3px" }}>{part}</mark>
  })
}

export function ApplicationProgressPanel({ application, userId, resumeId, user, onClose, onNeedsInput }) {
  const [docData, setDocData] = useState(application)
  const [loading, setLoading] = useState(true)
  const [stage, setStage] = useState(1)
  const [currentAction, setCurrentAction] = useState("Connecting...")
  const [pendingQuestion, setPendingQuestion] = useState("")
  const [waitingReply, setWaitingReply] = useState(false)
  const [isReview, setIsReview] = useState(false)
  const [isDone, setIsDone] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isReconnecting, setIsReconnecting] = useState(false)
  const [sessionStarting, setSessionStarting] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [leftTab, setLeftTab] = useState("resume")

  const appId = application?.id
  const optimizationData = docData?.optimizationData || null
  const resumeUrl = docData?.resumeUrl || ""
  const optimisedResumeId = docData?.optimisedResumeId || ""
  const jobUrl = docData?.jobUrl || application?.jobUrl || ""
  const description = docData?.description || application?.description || ""
  const sessionId = docData?.sessionId || application?.sessionId || ""

  useEffect(() => {
    if (!userId || !appId) return
    setLoading(true)
    const unsub = onSnapshot(
      doc(db, "users", userId, "applications", appId),
      snap => {
        const next = snap.exists() ? normalizeApplicationDoc(snap) : application
        setDocData(next)
        const isPreSession = next.status === "pending_optimization" || next.status === "pending_resume_review"
        if (isPreSession) {
          if (next.optimizationData && next.resumeUrl) {
            setStage(2)
            setCurrentAction("Resume optimised - review below")
          } else {
            setStage(1)
            setCurrentAction("Tailoring your resume to the job description.")
          }
        } else if (next.status === "completed") {
          setStage(5)
          setIsDone(true)
          setCurrentAction("Application submitted!")
        } else if (next.status === "failed") {
          setIsError(true)
          setCurrentAction("Application paused")
        } else {
          setStage(3)
          setCurrentAction("Connecting...")
        }
        setLoading(false)
      },
      err => {
        console.error("Failed to load application progress:", err)
        setIsError(true)
        setLoading(false)
      }
    )
    return () => unsub()
  }, [userId, appId])

  useEffect(() => {
    if (stage !== 1) return undefined
    const timer = setInterval(() => setElapsed(v => v + 1), 1000)
    return () => clearInterval(timer)
  }, [stage])

  useEffect(() => {
    if (!sessionId || stage < 3 || isDone) return undefined
    const es = new EventSource(`${APPLY_AGENT_API}/apply/events/${sessionId}`)
    es.onmessage = (event) => {
      if (!event.data || event.data === "[DONE]") return
      try {
        const ev = JSON.parse(event.data)
        handleProgressEvent(ev)
        if (["done", "cancelled"].includes(ev.type)) es.close()
      } catch {}
    }
    es.onerror = () => {
      setIsReconnecting(true)
      setCurrentAction("Reconnecting...")
    }
    return () => es.close()
  }, [sessionId, stage, isDone])

  const handleProgressEvent = (ev) => {
    const type = ev.type || ""
    const text = ev.text || ev.message || ""
    const toolLabels = {
      open_application: "Opening application",
      get_page_state: "Reading page",
      click_popup_option: "Selecting option",
      sign_in: "Signing in",
      create_account: "Creating account",
      fill_field: "Filling field",
      select_option: "Selecting option",
      check_checkbox: "Checking box",
      upload_resume: "Uploading resume",
      submit_step: "Submitting section",
      check_email_verified: "Verifying email",
      go_back: "Going back",
      close_browser: "Closing browser",
    }
    const toolStage = { open_application: 3, sign_in: 3, create_account: 3, check_email_verified: 3, fill_field: 3, select_option: 3, check_checkbox: 3, upload_resume: 3, submit_step: 4 }
    setIsReconnecting(false)
    if (type === "queued") {
      setCurrentAction(`Position #${ev.position || 1} in queue...`)
      setStage(s => Math.max(s, 3))
    } else if (type === "processing") {
      setCurrentAction("Starting application...")
      setStage(s => Math.max(s, 3))
    } else if (type === "tab") {
      const label = ev.label || text
      setCurrentAction(label)
      setStage(s => Math.max(s, label.toLowerCase().includes("submit") || label.toLowerCase().includes("review") ? 4 : 3))
    } else if (type === "status" || type === "message") {
      if (text) setCurrentAction(text)
    } else if (type === "question") {
      setPendingQuestion(text)
      setWaitingReply(true)
    } else if (type === "tool_start") {
      const tool = ev.tool || ""
      setCurrentAction(toolLabels[tool] || tool.replaceAll("_", " "))
      if (toolStage[tool]) setStage(s => Math.max(s, toolStage[tool]))
    } else if (type === "review") {
      setStage(4)
      setIsReview(true)
      setWaitingReply(false)
      setCurrentAction("Ready to submit")
    } else if (type === "done") {
      setStage(5)
      setIsDone(true)
      setIsReview(false)
      setWaitingReply(false)
      setCurrentAction("Application submitted!")
    } else if (type === "error") {
      setIsReconnecting(true)
      setCurrentAction("Reconnecting...")
    } else if (type === "cancelled") {
      setIsError(true)
      setCurrentAction("Session cancelled")
    }
  }

  const startSession = async (useOptimized) => {
    if (!jobUrl || !userId || !appId) return
    setSessionStarting(true)
    setIsError(false)
    try {
      const chosenResumeId = useOptimized && optimisedResumeId ? optimisedResumeId : resumeId
      const res = await fetch(`${APPLY_AGENT_API}/apply/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job_url: jobUrl, email: user?.email || "", password: DEFAULT_WORKDAY_PASSWORD, user_id: userId, resume_id: chosenResumeId || "", application_id: appId }),
      })
      if (!res.ok) throw new Error(`Failed to start session (${res.status})`)
      if (useOptimized) setDoc(doc(db, "users", userId, "applications", appId), { useOptimizedResume: true }, { merge: true }).catch(() => {})
      setStage(3)
      setCurrentAction("Agent will apply in the background")
      toast.success("Agent will apply in the background")
    } catch (err) {
      console.error("Failed to start application session:", err)
      setIsError(true)
      setCurrentAction("Failed to start application")
      toast.error("Failed to start application")
    } finally {
      setSessionStarting(false)
    }
  }

  const confirmSubmit = async () => {
    if (!sessionId) return
    setIsReview(false)
    setCurrentAction("Submitting...")
    await fetch(`${APPLY_AGENT_API}/apply/reply/${sessionId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ answers: [{ label: "__confirm__", value: "submit" }] }),
    })
  }

  const cancelSession = async () => {
    if (sessionId) {
      await fetch(`${APPLY_AGENT_API}/apply/cancel/${sessionId}`, { method: "POST", headers: { "Content-Type": "application/json" } }).catch(() => {})
    }
    onClose()
  }

  const title = isDone ? "Application submitted" : isError ? "Application paused" : isReview ? "Review and submit" : stage === 1 ? "Optimising your resume" : stage === 2 ? "Review your resume" : stage === 3 ? "Filling your application" : "Submitting application"
  const dotColor = isDone ? "#121212" : isError ? "#B44747" : "#D95F19"
  const meta = optimizationData?.optimizationMetadata || {}
  const keywords = Array.isArray(meta.keywords) ? meta.keywords : []
  const totalKeywords = meta.total || keywords.length
  const matchedCount = meta.matched || keywords.filter(k => k?.matched).length
  const hardKeywords = keywords.filter(k => (k?.type || "hard") === "hard")
  const softKeywords = keywords.filter(k => k?.type === "soft")
  const hardMissing = hardKeywords.filter(k => !k.matched)
  const hardCovered = hardKeywords.filter(k => k.matched)
  const softMissing = softKeywords.filter(k => !k.matched)
  const softCovered = softKeywords.filter(k => k.matched)
  const changes = Array.isArray(meta.changes) ? meta.changes : []

  return (
    <div className="application-detail-page" style={{ height: "100vh", margin: "-18px -24px -48px", overflow: "hidden", background: "#F9FAFB", padding: 0, fontFamily: FF, color: "#0F0C08" }}>
      <style>{`
        @keyframes pulseDot { 0% { transform: scale(0.8); opacity: .65; } 70% { transform: scale(2.2); opacity: 0; } 100% { opacity: 0; } }
        @keyframes appsShimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
        .apps-shimmer { background: linear-gradient(90deg, #F1F2F4 0%, #F7F8FA 50%, #F1F2F4 100%); background-size: 800px 100%; animation: appsShimmer 1.4s linear infinite; }
        .application-review-scroll::-webkit-scrollbar,
        .application-resume-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
        .application-review-scroll::-webkit-scrollbar-thumb,
        .application-resume-scroll::-webkit-scrollbar-thumb { background: rgba(15,12,8,.18); border-radius: 999px; }
        .application-resume-frame { display: block; background: #F9FAFB; }
        .resume-toolbar-button:hover { background: rgba(15,12,8,.05); color: rgba(15,12,8,.92); }
        @media (max-width: 980px) {
          .application-detail-page { height: auto !important; min-height: 100vh; overflow: visible !important; padding: 0 !important; margin: -12px -14px -40px !important; }
          .application-workspace { border-radius: 0 !important; border: 0 !important; box-shadow: none !important; }
          .application-main-split { grid-template-columns: 1fr !important; }
          .application-review-pane { width: 100% !important; height: auto !important; min-height: 560px; }
          .application-resume-pane { min-height: 720px; }
        }
      `}</style>

      <div className="application-workspace" style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", background: "#FFFFFF", border: 0, borderRadius: 0, boxShadow: "none" }}>
        <header style={{ height: 56, flexShrink: 0, display: "flex", alignItems: "center", gap: 14, padding: "0 16px", borderBottom: "0.8px solid rgba(15,12,8,0.08)", background: "#FFFFFF" }}>
          <button onClick={onClose} title="Back" style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 32, padding: "0 10px", border: 0, borderRadius: 999, background: "transparent", color: "#4B5563", fontSize: 12.5, fontWeight: 650, cursor: "pointer" }}>
            <ArrowLeft size={14} /> Back
          </button>
          <CompanyLogo app={docData || application} />
          <div style={{ minWidth: 0, flex: 1, lineHeight: 1.2 }}>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14, fontWeight: 700, color: "#111827" }}>{docData?.jobTitle || application?.jobTitle || "Role"}</div>
            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2, fontSize: 12, color: "#9CA3AF" }}>{docData?.company || application?.company || "Company"}</div>
          </div>
          {jobUrl && (
            <button onClick={() => window.open(jobUrl, "_blank")} style={{ height: 30, padding: "0 11px", border: "1px solid #E5E7EB", borderRadius: 999, background: "#FFFFFF", color: "#4B5563", fontSize: 12, fontWeight: 650, cursor: "pointer" }}>View job posting</button>
          )}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 12px", borderRadius: 999, background: stage === 2 ? "#F5F0E6" : "#F4F4F5", color: stage === 2 ? "#121212" : "#52525B", fontSize: 12, fontWeight: 750 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: stage === 2 ? "#121212" : dotColor }} />
            {stage === 2 ? "Ready to review" : isDone ? "Submitted" : isError ? "Paused" : PROGRESS_STAGES[Math.max(0, Math.min(4, stage - 1))]}
          </span>
        </header>

        <div className="application-main-split" style={{ minHeight: 0, flex: 1, display: "grid", gridTemplateColumns: "minmax(420px, 38%) minmax(0, 1fr)" }}>
          <aside className="application-review-pane" style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", borderRight: "1px solid #F3F4F6", background: "#FFFFFF" }}>
            <div style={{ height: 48, flexShrink: 0, display: "flex", alignItems: "stretch", gap: 22, padding: "0 16px", borderBottom: "1px solid #F3F4F6" }}>
              {[{ id: "resume", label: "Resume" }, { id: "jd", label: "JD" }].map(tab => {
                const active = leftTab === tab.id
                return (
                  <button key={tab.id} type="button" onClick={() => setLeftTab(tab.id)} style={{ position: "relative", border: 0, background: "transparent", color: active ? "#111827" : "#9CA3AF", fontSize: 13, fontWeight: active ? 700 : 650, display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer" }}>
                    {tab.label}
                    {active && <span style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 2, borderRadius: 999, background: "#121212" }} />}
                  </button>
                )
              })}
            </div>

            <div className="application-review-scroll" style={{ minHeight: 0, flex: 1, overflowY: "auto", padding: "18px 20px 22px" }}>
              {loading ? <ReviewPaneShimmer /> : leftTab === "jd" ? (
                <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "#374151", whiteSpace: "pre-wrap" }}>
                  {description ? highlightDescription(description, keywords) : <span style={{ color: "#9CA3AF" }}>No job description available.</span>}
                </div>
              ) : (
                <>
                  {stage === 1 && <OptimisingShimmer currentAction={currentAction} />}

                  {stage === 2 && (
                    <>
                      <MatchHero percentage={meta.percentage || 0} matched={matchedCount} total={totalKeywords || 0} />

                      {hardMissing.length > 0 && (
                        <>
                          <SectionLabel count={hardMissing.length}>Missing hard skills</SectionLabel>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {hardMissing.map((kw, i) => <KeywordChip key={i} label={kw.keyword} matched={false} />)}
                          </div>
                        </>
                      )}

                      {(hardCovered.length > 0 || softKeywords.length > 0) && <SectionLabel>More coverage</SectionLabel>}
                      {hardCovered.length > 0 && (
                        <Collapsible title="Covered hard skills" count={hardCovered.length}>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingLeft: 18 }}>
                            {hardCovered.map((kw, i) => <KeywordChip key={i} label={kw.keyword} matched />)}
                          </div>
                        </Collapsible>
                      )}
                      {softKeywords.length > 0 && (
                        <Collapsible title="Soft skills" count={softKeywords.length}>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, paddingLeft: 18 }}>
                            {softCovered.map((kw, i) => <KeywordChip key={`sc-${i}`} label={kw.keyword} matched />)}
                            {softMissing.map((kw, i) => <KeywordChip key={`sm-${i}`} label={kw.keyword} muted />)}
                          </div>
                        </Collapsible>
                      )}

                      {changes.length > 0 && (
                        <>
                          <SectionLabel count={changes.length}>What changed</SectionLabel>
                          <div style={{ paddingLeft: 2 }}>
                            {changes.map((c, i) => (
                              <ChangeEntry key={i} change={c} isLast={i === changes.length - 1} />
                            ))}
                          </div>
                        </>
                      )}

                    </>
                  )}

                  {stage >= 3 && (
                    <div>
                      <ProgressRail stage={stage} color={dotColor} />
                      <ActiveProgress currentAction={currentAction} dotColor={dotColor} isDone={isDone} isError={isError} waitingReply={waitingReply} pendingQuestion={pendingQuestion} onNeedsInput={onNeedsInput} isReview={isReview} confirmSubmit={confirmSubmit} description={description} />
                    </div>
                  )}
                </>
              )}
            </div>

            {!loading && stage === 2 && (
              <div style={{ flexShrink: 0, padding: "12px 16px", borderTop: "1px solid #F3F4F6", background: "#FFFFFF", boxShadow: "0 -4px 12px rgba(15,12,8,0.03)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <button disabled={sessionStarting} onClick={() => startSession(false)} style={{ border: 0, background: "transparent", color: "#6B7280", padding: "10px 4px", fontSize: 12.5, fontWeight: 650, cursor: sessionStarting ? "default" : "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>Use original resume</button>
                  <button disabled={sessionStarting} onClick={() => startSession(true)} style={{ marginLeft: "auto", border: 0, background: "#121212", color: "#FFFFFF", borderRadius: 999, padding: "11px 22px", fontSize: 13.5, fontWeight: 750, cursor: sessionStarting ? "default" : "pointer", boxShadow: "0 2px 8px rgba(18,18,18,0.18)" }}>{sessionStarting ? "Starting…" : "Approve & submit"}</button>
                </div>
              </div>
            )}
          </aside>

          <section className="application-resume-pane" style={{ minWidth: 0, height: "100%", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: "#F9FAFB" }}>
            <div style={{ minHeight: 42, flexShrink: 0, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "6px 12px", borderBottom: "1px solid #F3F4F6", background: "#FFFFFF" }}>
              <span style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 650 }}>Template</span>
              <div style={{ display: "inline-flex", gap: 2, padding: 2, borderRadius: 6, background: "#F3F4F6" }}>
                <button className="resume-toolbar-button" style={{ border: 0, borderRadius: 5, background: "#FFFFFF", color: "#111827", padding: "5px 9px", fontSize: 12, fontWeight: 650, boxShadow: "0 1px 2px rgba(0,0,0,.04)" }}>Standard</button>
                <button className="resume-toolbar-button" style={{ border: 0, borderRadius: 5, background: "transparent", color: "#6B7280", padding: "5px 9px", fontSize: 12, fontWeight: 650 }}>Jake</button>
              </div>
              <span style={{ width: 1, height: 20, background: "#E5E7EB", margin: "0 4px" }} />
              <button className="resume-toolbar-button" style={{ border: "1px solid #E5E7EB", borderRadius: 6, background: "#FFFFFF", color: "#4B5563", padding: "6px 9px", fontSize: 12, fontWeight: 650 }}>Garamond</button>
              <span style={{ width: 1, height: 20, background: "#E5E7EB", margin: "0 4px" }} />
              <span style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 650 }}>Size</span>
              <button className="resume-toolbar-button" style={{ border: "1px solid #E5E7EB", borderRadius: 6, background: "#FFFFFF", color: "#4B5563", padding: "5px 8px", fontSize: 12 }}>-</button>
              <span style={{ minWidth: 42, textAlign: "center", fontSize: 12, fontWeight: 700, color: "#374151" }}>10.5pt</span>
              <button className="resume-toolbar-button" style={{ border: "1px solid #E5E7EB", borderRadius: 6, background: "#FFFFFF", color: "#4B5563", padding: "5px 8px", fontSize: 12 }}>+</button>
              <span style={{ width: 1, height: 20, background: "#E5E7EB", margin: "0 4px" }} />
              <button className="resume-toolbar-button" style={{ border: 0, borderRadius: 6, background: "transparent", color: "#4B5563", padding: "6px 9px", fontSize: 12, fontWeight: 650 }}>Fit to one page</button>
            </div>

            <div style={{ position: "relative", minHeight: 0, flex: 1, overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 16, left: 32, zIndex: 3 }}>
                <span style={{ display: "inline-flex", alignItems: "center", height: 26, padding: "0 12px", border: "1px solid #F3F4F6", borderRadius: 999, background: "#FFFFFF", color: "#121212", fontSize: 11.5, fontWeight: 750, boxShadow: "0 1px 3px rgba(20,20,19,0.06)" }}>{matchedCount || docData?.reviewSections?.length || 0} edits · tailored</span>
              </div>
              {resumeUrl && (
                <div style={{ position: "absolute", top: 14, right: 32, zIndex: 3, display: "flex", alignItems: "center", gap: 2, padding: 4, border: "1px solid #F3F4F6", borderRadius: 12, background: "rgba(255,255,255,.96)", boxShadow: "0 1px 4px rgba(20,20,19,0.08)" }}>
                  <button type="button" className="resume-toolbar-button" title="Edit" style={{ display: "inline-flex", alignItems: "center", gap: 6, border: 0, borderRadius: 8, background: "transparent", color: "#374151", padding: "7px 10px", fontSize: 12, fontWeight: 750, cursor: "pointer" }}>
                    <Pencil size={14} /> Edit
                  </button>
                  <a className="resume-toolbar-button" href={resumeUrl} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 8, color: "#374151", padding: "7px 10px", textDecoration: "none", fontSize: 12, fontWeight: 750 }}>
                    <Download size={14} /> Download
                  </a>
                </div>
              )}
              <div className="application-resume-scroll" style={{ width: "100%", height: "100%", overflow: "auto", padding: "56px 40px 40px", background: "#F9FAFB" }}>
                {resumeUrl ? (
                  <ResumeCanvas url={resumeUrl} />
                ) : (
                  <div style={{ width: "min(8.5in, 100%)", height: "calc(100vh - 154px)", minHeight: 520, margin: "0 auto", borderRadius: 12, background: "#FFFFFF", boxShadow: "0 4px 28px rgba(18,18,18,0.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280", fontSize: 13, fontWeight: 700 }}>
                    Resume preview will appear here once tailoring is complete.
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

function KeywordGroup({ title, items }) {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 11, fontWeight: 800, color: "#64748B" }}>
        <span>{title}</span>
        <span>{items.filter(kw => kw.matched).length}/{items.length}</span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        {items.map((kw, i) => (
          <span key={i} style={{ padding: "5px 9px", borderRadius: 6, background: "#F3F2ED", color: kw.matched ? "#0F172A" : "#64748B", fontSize: 11.5, fontWeight: 600 }}>{kw.matched ? "✓ " : "○ "}{kw.keyword || kw}</span>
        ))}
      </div>
    </>
  )
}

function ActiveProgress({ currentAction, dotColor, isDone, isError, waitingReply, pendingQuestion, onNeedsInput, isReview, confirmSubmit, description }) {
  return (
    <>
      <section style={{ padding: 16, marginBottom: 12, borderRadius: 16, background: "#FFFFFF", border: "1px solid #DEE1E5" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: dotColor, fontSize: 13, fontWeight: 800 }}>
          {!isDone && !isError && !waitingReply ? <PulsingDot color={dotColor} /> : <span style={{ width: 8, height: 8, borderRadius: "50%", background: dotColor }} />}
          {isDone ? "Completed" : isError ? "Error" : waitingReply ? "Needs your input" : "Agent working"}
        </div>
        <p style={{ margin: "8px 0 0", fontSize: 13, color: "#64748B", lineHeight: 1.55 }}>{currentAction}</p>
      </section>
      {waitingReply && pendingQuestion && (
        <button onClick={onNeedsInput} style={{ width: "100%", textAlign: "left", padding: 16, marginBottom: 12, borderRadius: 12, background: "#FFFFFF", border: "1px solid rgba(232,112,42,0.24)", color: "#0F172A", cursor: "pointer", fontSize: 14, lineHeight: 1.5 }}>{pendingQuestion}</button>
      )}
      {isReview && (
        <section style={{ padding: 16, marginBottom: 12, borderRadius: 16, background: "#FFFFFF", border: "1px solid #DEE1E5" }}>
          <h3 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 800 }}>Ready to submit</h3>
          <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748B" }}>The agent has filled your application. Confirm to submit.</p>
          <button onClick={confirmSubmit} style={{ width: "100%", height: 50, borderRadius: 12, border: "none", background: "#0F172A", color: "#FFFFFF", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>Submit application</button>
        </section>
      )}
      {description && (
        <section style={{ padding: 16, borderTop: "1px solid #DEE1E5" }}>
          <h3 style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: "0.08em", color: "#64748B" }}>JOB DESCRIPTION</h3>
          <p style={{ margin: 0, fontSize: 13, color: "#64748B", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{description.slice(0, 1400)}</p>
        </section>
      )}
    </>
  )
}

function SelectBox({ checked, indeterminate, onClick, ...rest }) {
  const active = checked || indeterminate
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? "mixed" : !!checked}
      onClick={onClick}
      {...rest}
      style={{
        width: 18, height: 18, borderRadius: 4,
        border: active ? "1.5px solid #121212" : "1.5px solid #d4d4d8",
        background: active ? "#121212" : "#ffffff",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", padding: 0,
        transition: "background 0.12s, border-color 0.12s",
      }}
    >
      {indeterminate ? (
        <span style={{ width: 8, height: 2, background: "#F5F0E6", borderRadius: 1 }} />
      ) : checked ? (
        <Check size={12} strokeWidth={3} color="#F5F0E6" />
      ) : null}
    </button>
  )
}

export default function ApplicationsTab({ userId, user, resumeId }) {
  const [applications, setApplications] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [filter, setFilter] = useState("review")
  const [progressApplication, setProgressApplication] = useState(null)
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const initialFilterSet = useRef(false)

  useEffect(() => {
    if (!userId) return
    setLoaded(false)
    const unsub = onSnapshot(
      query(collection(db, "users", userId, "applications"), orderBy("appliedAt", "desc")),
      snap => {
        setApplications(snap.docs.map(normalizeApplicationDoc))
        setLoaded(true)
      },
      err => {
        console.error("Failed to watch applications:", err)
        setLoaded(true)
      }
    )
    return () => unsub()
  }, [userId])

  const groups = useMemo(() => buckets(applications), [applications])
  useEffect(() => {
    if (!loaded || initialFilterSet.current) return
    initialFilterSet.current = true
    if (groups.review.length > 0) setFilter("review")
    else if (groups.inProgress.length > 0) setFilter("inProgress")
    else if (groups.submitted.length > 0) setFilter("submitted")
    else if (groups.failed.length > 0) setFilter("failed")
  }, [loaded, groups])

  const visible = useMemo(() => {
    return groups[filter] || []
  }, [groups, filter])

  const openApplication = (app) => {
    setProgressApplication(app)
  }

  const deleteApplication = async (id) => {
    if (!userId || !id) return
    try {
      await deleteDoc(doc(db, "users", userId, "applications", id))
    } catch (err) {
      console.error("Failed to delete application:", err)
      toast.error("Could not delete application")
    }
  }

  // Clear selection when filter changes (ids may no longer be visible)
  useEffect(() => { setSelectedIds(new Set()) }, [filter])

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const visibleIds = useMemo(() => visible.map(a => a.id), [visible])
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id))
  const someVisibleSelected = visibleIds.some(id => selectedIds.has(id))

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (allVisibleSelected) {
        const next = new Set(prev)
        visibleIds.forEach(id => next.delete(id))
        return next
      }
      const next = new Set(prev)
      visibleIds.forEach(id => next.add(id))
      return next
    })
  }

  const deleteSelected = async () => {
    if (!userId || selectedIds.size === 0) return
    const ids = [...selectedIds]
    try {
      await Promise.all(ids.map(id => deleteDoc(doc(db, "users", userId, "applications", id))))
      setSelectedIds(new Set())
      toast.success(`Deleted ${ids.length} application${ids.length === 1 ? "" : "s"}`)
    } catch (err) {
      console.error("Failed to delete selected applications:", err)
      toast.error("Could not delete selected applications")
    }
  }

  const retryApplication = async (app) => {
    if (!userId || !app.jobUrl) return
    try {
      const res = await fetch(APPLICATIONS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, jobUrl: app.jobUrl, jobTitle: app.jobTitle, company: app.company, logoUrl: app.logoUrl || "", description: app.description || "", fcmToken: "" }),
      })
      if (res.status !== 201) throw new Error(`Application API error ${res.status}`)
      await deleteApplication(app.id)
      toast.success(`Agent Apply restarted for ${app.company}`)
      setFilter("inProgress")
    } catch (err) {
      console.error("Failed to retry application:", err)
      toast.error("Could not restart application")
    }
  }

  if (progressApplication) {
    return (
      <ApplicationProgressPanel
        application={progressApplication}
        userId={userId}
        resumeId={resumeId}
        user={user}
        onClose={() => setProgressApplication(null)}
        onNeedsInput={() => {
          setProgressApplication(null)
          toast("Additional-info handoff is coming to web next.", { icon: "⏳" })
        }}
      />
    )
  }

  return (
    <div style={{ fontFamily: FF }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulseDot { 0% { transform: scale(0.8); opacity: .65; } 70% { transform: scale(2.2); opacity: 0; } 100% { opacity: 0; } }
        @keyframes appsShimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
        .apps-shimmer { background: linear-gradient(90deg, #F1F2F4 0%, #F7F8FA 50%, #F1F2F4 100%); background-size: 800px 100%; animation: appsShimmer 1.4s linear infinite; }
        .apps-row:hover { background: rgba(249,250,251,.6); }
        .apps-table tbody tr:last-child { border-bottom: 0 !important; }
        .apps-primary-action:hover { filter: brightness(1.1); }
        .apps-action { display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 14px; border-radius: 9999px; border: none; font-family: ${FF}; font-size: 12px; font-weight: 600; letter-spacing: -0.005em; cursor: pointer; white-space: nowrap; transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease; }
        .apps-action svg { transition: transform 0.2s ease; }
        .apps-action:hover svg { transform: translateX(1px); }
        .apps-action.approve { background: #121212; color: #F5F0E6; }
        .apps-action.approve:hover { background: #000000; }
        .apps-action.reapply { background: #ffffff; color: #27272a; border: 1px solid #e4e4e7; }
        .apps-action.reapply:hover { background: #fafafa; border-color: #d4d4d8; }
        .apps-action.reapply:hover svg { transform: rotate(-45deg); }
        @keyframes apps-opt-pulse { 0%,100% { opacity: 0.85 } 50% { opacity: 1 } }
        .apps-optimising { display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 12px; border-radius: 9999px; background: #E0F2FE; color: #0369A1; font-family: ${FF}; font-size: 11.5px; font-weight: 700; letter-spacing: -0.005em; white-space: nowrap; animation: apps-opt-pulse 1.6s ease-in-out infinite; }
        .apps-optimising svg { animation: apps-opt-pulse 1.6s ease-in-out infinite; }
        @media (max-width: 820px) {
          .apps-table-scroll { overflow-x: auto; }
          .apps-table { min-width: 760px; }
        }
      `}</style>
      <div className="dash-filter-bar-wrap" style={{ padding: "8px 0 18px", marginBottom: 4 }}>
        <div className="dash-filter-head" style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: "-0.025em", color: "#18181b", lineHeight: 1.15 }}>
              <span style={{ fontWeight: 800 }}>{applications.length.toLocaleString()}</span> Applications
            </h2>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#71717a", fontWeight: 500 }}>
              {groups.review.length > 0
                ? `${applications.length} application${applications.length === 1 ? "" : "s"} · ${groups.review.length} need${groups.review.length === 1 ? "s" : ""} review`
                : applications.length === 0
                  ? "Applications your agent has queued or sent"
                  : `${applications.length} application${applications.length === 1 ? "" : "s"} tracked`}
            </p>
          </div>
          {selectedIds.size > 0 && (
            <button
              onClick={deleteSelected}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                height: 34, padding: "0 14px", borderRadius: 9999,
                background: "#121212", color: "#F5F0E6",
                border: "none", cursor: "pointer",
                fontFamily: FF, fontSize: 12.5, fontWeight: 600,
                whiteSpace: "nowrap", flexShrink: 0,
              }}
            >
              <Trash2 size={14} strokeWidth={2.2} />
              Delete {selectedIds.size}
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "inline-flex", gap: 4, padding: 5, borderRadius: 24, background: "#FFFFFF", border: "1px solid #ececec", overflowX: "auto", maxWidth: "100%", marginBottom: 12 }}>
          {FILTERS.map(item => {
            const selected = filter === item.id
            return (
              <button key={item.id} onClick={() => setFilter(item.id)} style={{ display: "inline-flex", alignItems: "center", gap: 5, height: 30, padding: "0 12px", border: "none", borderRadius: 20, background: selected ? "#121212" : "transparent", color: selected ? "#F5F0E6" : "#64748B", fontSize: 12, fontWeight: selected ? 700 : 600, whiteSpace: "nowrap", cursor: "pointer" }}>
                <span>{groups[item.id]?.length || 0}</span>
                <span>{item.label}</span>
              </button>
            )
          })}
      </div>

      {!loaded ? (
        <div className="apps-table-scroll" style={{ background: "#FFFFFF", border: "1px solid #eeeeee", borderRadius: 8, overflow: "hidden", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
          <TableShimmer />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <div className="apps-table-scroll" style={{ background: "#FFFFFF", border: "1px solid #eeeeee", borderRadius: 8, overflow: "hidden", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>{(
          <table className="apps-table" style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontFamily: FF, fontSize: 13 }}>
            <colgroup>
              <col style={{ width: 44 }} />
              <col style={{ width: "40%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "16%" }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ borderBottom: "1px solid #e5e7eb", background: "#f9fafb", padding: "10px 8px 10px 16px", textAlign: "left" }}>
                  <SelectBox
                    checked={allVisibleSelected}
                    indeterminate={!allVisibleSelected && someVisibleSelected}
                    onClick={(e) => { e.stopPropagation(); toggleSelectAll() }}
                    aria-label={allVisibleSelected ? "Clear selection" : "Select all"}
                  />
                </th>
                {["Company", "Resume", "Status", "Applied", ""].map((heading, index) => (
                  <th key={heading || "actions"} style={{ borderBottom: "1px solid #e5e7eb", background: "#f9fafb", padding: index === 4 ? "10px 8px" : "10px 16px", textAlign: "left", fontWeight: 500, fontSize: 10.5, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(app => {
                const resume = resumeMeta(app.status)
                const status = statusMeta(app.status)
                const isOptimising = app.status === "pending_optimization"
                const canApprove = !isOptimising && ["pending_resume_review", "additional_information_required", "pending_verification", "credentials_error"].includes(app.status)
                const canReapply = ["failed", "job_expired", "skipped"].includes(app.status) && app.jobUrl
                const isSelected = selectedIds.has(app.id)
                return (
                  <tr key={app.id} className="apps-row" onClick={() => openApplication(app)} style={{ cursor: "pointer", borderBottom: "1px solid #f3f4f6", transition: "background 160ms ease", background: isSelected ? "#fafafa" : undefined }}>
                    <td style={{ padding: "12px 8px 12px 16px" }}>
                      <SelectBox
                        checked={isSelected}
                        onClick={(e) => { e.stopPropagation(); toggleSelect(app.id) }}
                        aria-label={isSelected ? "Deselect" : "Select"}
                      />
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <CompanyLogo app={app} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: "#111827", lineHeight: 1.22, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{app.company || "Company"}</div>
                          <div title={app.jobTitle || "Untitled role"} style={{ marginTop: 3, fontSize: 12, color: "#6b7280", lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{app.jobTitle || "Untitled role"}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ display: "inline-flex" }}><Pill meta={resume} /></div>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <Pill meta={status} />
                    </td>
                    <td style={{ padding: "12px 16px", whiteSpace: "nowrap", fontSize: 12, color: "#6b7280" }}>{ago(app.appliedAt)}</td>
                    <td style={{ padding: "12px 8px" }}>
                      <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        {isOptimising && (
                          <span className="apps-optimising" aria-label="Optimising resume">
                            <Sparkles size={13} strokeWidth={2.2} />
                            Optimising Resume
                          </span>
                        )}
                        {canApprove && (
                          <button
                            className="apps-action approve"
                            onClick={(e) => { e.stopPropagation(); setProgressApplication(app) }}
                            title="Approve resume and submit"
                          >
                            <Check size={13} strokeWidth={2.6} />
                            Approve
                          </button>
                        )}
                        {canReapply && (
                          <button
                            className="apps-action reapply"
                            onClick={(e) => { e.stopPropagation(); retryApplication(app) }}
                            title="Reapply with a fresh session"
                          >
                            <RefreshCw size={13} strokeWidth={2.4} />
                            Reapply
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}</div>
      )}

    </div>
  )
}
